import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Camera, Check, ExternalLink, FileText, Images, Layers, PackageOpen, ScanLine, Search, Trash2, X } from 'lucide-react';
import TopBar from '../components/TopBar';
import Sheet from '../components/Sheet';
import PickOrdersSheet from '../components/PickOrdersSheet';
import SelfieStudio from '../components/SelfieStudio';
import DeliveryNoteSheet from '../components/DeliveryNoteSheet';
import ClaimForm from '../components/ClaimForm';
import OtherOrders from '../components/OtherOrders';
import PickPrinter from '../components/PickPrinter';
import { api, errorMessage } from '../lib/api';
import { loadOrdersCache, saveOrdersCache } from '../lib/store';
import { shrinkPhoto } from '../lib/photos';
import { cn, fmtDate } from '../lib/utils';

const MAX_PHOTOS = 60;
const timeOf = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/* Three modes. Delivery note: photograph the supplier's packing list page by
   page, pick the orders it covers, and Inny merges the pages into one PDF,
   attaches it to each order in Odoo with a note, and logs it. Claim: a
   parcel without a PO that belongs to after-sales, posted to #sav-warehouse.
   Selfie: the warehouse photo booth. */
export default function Photos() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromReceipt = Boolean(location.state?.po_ids); // opened from a receipt: Back returns to it
  const [mode, setMode] = useState(['selfie', 'claim'].includes(location.state?.mode) ? location.state.mode : 'note');
  const [photos, setPhotos] = useState([]); // { id, blob, url, width, height, pos: [po ids] (mixed pallet) }
  const [mixed, setMixed] = useState(false); // mixed pallet: each page names its own orders
  const [noNote, setNoNote] = useState(false); // the goods came without paperwork: no photos, the office is alerted
  const [tagging, setTagging] = useState(null); // photo id whose orders are being chosen
  const [orders, setOrders] = useState(() => loadOrdersCache()?.data?.orders || []);
  const [chosen, setChosen] = useState(() => location.state?.po_ids || []);
  const [picking, setPicking] = useState(false);
  const [note, setNote] = useState('');
  const [labels, setLabels] = useState({}); // { po_id: '3' }: warehouse labels per order
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState(null);
  const [more, setMore] = useState(false); // older notes than the ones listed
  const [search, setSearch] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewing, setViewing] = useState(null); // delivery note id whose read lines are open
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const submitId = useRef(null); // this press of Save: the same again after a lost answer, a new one once anything changes
  useEffect(() => {
    submitId.current = null;
  }, [photos, chosen, noNote, note, labels]);

  const searchRef = useRef('');
  searchRef.current = search.trim();
  const loadRecent = useCallback(async () => {
    const q = searchRef.current;
    try {
      const d = await api.deliveryNotes({ q });
      if (q !== searchRef.current) return; // typed on meanwhile
      setRecent(d.notes);
      setMore(d.more);
    } catch {
      setRecent([]);
      setMore(false);
    }
  }, []);
  const loadOlder = async () => {
    const q = searchRef.current;
    setLoadingMore(true);
    try {
      const d = await api.deliveryNotes({ q, before: recent[recent.length - 1].id });
      if (q === searchRef.current) {
        setRecent((r) => [...r, ...d.notes]);
        setMore(d.more);
      }
    } catch {
      /* the button stays */
    } finally {
      setLoadingMore(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(loadRecent, 300);
    return () => clearTimeout(t);
  }, [search, loadRecent]);
  useEffect(() => {
    // the saved list shows at once; a fresh one follows, or orders confirmed
    // since it was saved can't be picked
    api
      .orders()
      .then((d) => {
        setOrders(d.orders);
        saveOrdersCache(d);
      })
      .catch(() => {});
  }, []);

  const addFiles = async (files) => {
    const room = MAX_PHOTOS - photos.length;
    const list = [...files].slice(0, Math.max(0, room));
    if (!list.length) return;
    setBusy(true);
    setProgress(`Preparing ${list.length === 1 ? 'the photo' : `${list.length} photos`}…`);
    try {
      const prepared = [];
      for (const f of list) prepared.push({ id: `${Date.now()}-${Math.random()}`, ...(await shrinkPhoto(f)) });
      setPhotos((p) => [...p, ...prepared.map((x) => ({ ...x, pos: [] }))]);
      if (mixedOn) setTagging(prepared[0].id);
    } catch (err) {
      setError(errorMessage(err, 'That photo could not be read'));
    } finally {
      setBusy(false);
      setProgress('');
    }
  };
  const onInput = (e) => {
    const files = [...(e.target.files || [])]; // copy before the reset empties the live list
    e.target.value = '';
    if (files.length) addFiles(files);
  };
  const remove = (id) =>
    setPhotos((p) => {
      const gone = p.find((x) => x.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return p.filter((x) => x.id !== id);
    });

  const chosenOrders = chosen.map((id) => orders.find((o) => o.id === id) || { id, name: `#${id}` });
  const mixedOn = mixed && chosen.length > 1;
  const short = (id) => (chosenOrders.find((o) => o.id === id)?.name || `#${id}`).split(' ')[0];
  // mixed pallet: every page names its orders and every order has a page
  const untagged = mixedOn ? photos.findIndex((p) => !p.pos.length) : -1;
  const pageless = mixedOn && photos.length ? chosenOrders.find((o) => !photos.some((p) => p.pos.includes(o.id))) : null;
  const saveTags = (id, ids) => {
    setPhotos((ps) => ps.map((x) => (x.id === id ? { ...x, pos: ids } : x)));
    // straight on to the next page still without orders (several from the gallery)
    const next = photos.find((x) => x.id !== id && !x.pos.length);
    setTagging(next ? next.id : null);
  };
  useEffect(() => {
    // orders dropped from the delivery leave the pages too
    setPhotos((ps) => (ps.some((x) => x.pos.some((id) => !chosen.includes(id))) ? ps.map((x) => ({ ...x, pos: x.pos.filter((id) => chosen.includes(id)) })) : ps));
  }, [chosen]);
  const suppliers = [...new Set(chosenOrders.map((o) => o.supplier).filter(Boolean))];

  const submit = async () => {
    setBusy(true);
    setError(null);
    setProgress(noNote ? 'Receiving the orders and alerting the office…' : `Merging ${photos.length} ${photos.length === 1 ? 'page' : 'pages'} and attaching to ${chosen.length} ${chosen.length === 1 ? 'order' : 'orders'}…`);
    try {
      submitId.current = submitId.current || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      const form = new FormData();
      form.append('submit_id', submitId.current);
      if (noNote) form.append('no_note', '1');
      else photos.forEach((p, i) => form.append('photos', p.blob, `page-${i + 1}.jpg`));
      form.append('po_ids', chosen.join(','));
      form.append('note', note.trim());
      chosen.forEach((id) => form.append(`labels_${id}`, (labels[id] || '').trim()));
      if (mixedOn) form.append('page_pos', JSON.stringify(photos.map((p) => p.pos)));
      const created = await api.createDeliveryNote(form);
      setResult(created);
      photos.forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos([]);
      setNote('');
      setLabels({});
      setNoNote(false);
      setMixed(false); // the next delivery starts plain
      loadRecent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  // after saving, the note is read in the background (a minute or two), then
  // checked for other open orders on it: follow it while the result shows
  const resultId = result?.id;
  const waiting = result != null && !result.no_note && result.other_orders == null && !['off', 'failed'].includes(result.read);
  useEffect(() => {
    if (!resultId || !waiting) return undefined;
    let tries = 0;
    const t = setInterval(async () => {
      if (++tries > 60) return clearInterval(t);
      try {
        const n = await api.deliveryNote(resultId);
        setResult((r) => (r?.id === n.id ? n : r));
      } catch {
        /* next tick */
      }
    }, 5000);
    return () => clearInterval(t);
  }, [resultId, waiting]);

  // every order on the note in one receipt: a big delivery of one brand over several POs
  const receive = (n) => navigate(`/receive/${n.po_ids.join(',')}`);

  const replaceNote = (n) => {
    setResult((r) => (r?.id === n.id ? n : r));
    setRecent((list) => list?.map((x) => (x.id === n.id ? n : x)));
  };

  const reset = () => {
    setResult(null);
    setChosen([]);
  };

  return (
    <div className="min-h-screen pb-10">
      <TopBar
        title={mode === 'note' ? 'Delivery note' : mode === 'claim' ? 'Claim' : 'Selfie'}
        subtitle={mode === 'note' ? "Photograph the supplier's packing list, attach it to the orders" : mode === 'claim' ? 'A parcel without a PO, for after-sales' : 'The warehouse photo booth'}
        onBack={() => (fromReceipt ? navigate(-1) : navigate('/'))}
      >
        <div className="mx-auto flex max-w-lg gap-1.5 px-3 pb-2" role="tablist" aria-label="Mode">
          {[
            ['note', 'Delivery note', FileText],
            ['claim', 'Claim', PackageOpen],
            ['selfie', 'Selfie', Camera],
          ].map(([key, label, Icon]) => (
            <button key={key} type="button" role="tab" aria-selected={mode === key} onClick={() => setMode(key)} className={cn('inline-flex h-10 items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold', mode === key ? 'bg-ink text-bg' : 'bg-surface text-ink2 active:bg-sunken')}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </TopBar>

      <main className="mx-auto max-w-lg px-3">
        {mode === 'selfie' && <SelfieStudio />}
        {mode === 'claim' && <ClaimForm />}
        {mode === 'note' && !result && (
          <>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onInput} />
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onInput} />

            {/* 1. the orders */}
            <section className="mt-3 rounded-xl bg-surface p-3">
              <h2 className="text-[13px] font-semibold text-ink2">1 · Orders on this delivery</h2>
              {chosenOrders.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {chosenOrders.map((o) => (
                    <button key={o.id} type="button" onClick={() => setChosen((c) => c.filter((id) => id !== o.id))} className="inline-flex h-8 items-center gap-1 rounded-full bg-ink pl-3 pr-2 text-[13px] font-semibold text-bg active:opacity-80" aria-label={`Remove ${o.name}`}>
                      <span className="font-cond text-[15px]">{o.name.split(' ')[0]}</span>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              )}
              {suppliers.length > 0 && <p className="mt-1.5 text-[12px] text-ink2">{suppliers.join(' · ')}</p>}
              <button type="button" onClick={() => setPicking(true)} className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-sunken text-[14px] font-semibold active:bg-line">
                <Layers className="h-4 w-4" /> {chosen.length ? 'Change the orders' : 'Pick the orders'}
              </button>
            </section>

            {/* 2. the pages */}
            <section className="mt-3 rounded-xl bg-surface p-3">
              <div className="flex items-baseline justify-between">
                <h2 className="text-[13px] font-semibold text-ink2">2 · Pages</h2>
                {!noNote && <span className="text-[12px] text-ink2">{photos.length ? `${photos.length} of ${MAX_PHOTOS}` : 'one photo per page'}</span>}
              </div>
              {!photos.length && (
                <label className="mt-2 flex items-start gap-3 rounded-xl bg-sunken px-3 py-2.5">
                  <input type="checkbox" checked={noNote} onChange={(e) => setNoNote(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-current" />
                  <span>
                    <span className="block text-[14px] font-semibold">No delivery note</span>
                    <span className="block text-[12px] text-ink2">
                      {noNote ? 'Find the order in Odoo and pick it above. The orders are received and the office is told to look for the paperwork.' : 'Tick this when the goods came without any paperwork.'}
                    </span>
                  </span>
                </label>
              )}
              {!noNote && chosen.length > 1 && (
                <label className="mt-2 flex items-start gap-3 rounded-xl bg-sunken px-3 py-2.5">
                  <input type="checkbox" checked={mixed} onChange={(e) => setMixed(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-current" />
                  <span>
                    <span className="block text-[14px] font-semibold">Mixed pallet</span>
                    <span className="block text-[12px] text-ink2">
                      {mixed ? 'After each photo, pick the orders on that page. Each order gets only its own pages.' : 'Off: every page goes on every order. Turn on when the pages belong to different orders.'}
                    </span>
                  </span>
                </label>
              )}
              {photos.length > 0 && (
                <ul className="-mx-1 mt-2 flex gap-2 overflow-x-auto px-1 pb-1">
                  {photos.map((p, i) => (
                    <li key={p.id} className="relative shrink-0">
                      <img src={p.url} alt={`Page ${i + 1}`} className="h-28 w-20 rounded-lg object-cover" />
                      <span className="absolute left-1 top-1 rounded-md bg-ink/80 px-1.5 text-[11px] font-bold text-bg">{i + 1}</span>
                      {mixedOn && (
                        <button type="button" onClick={() => setTagging(p.id)} aria-label={`Orders on page ${i + 1}`} className={cn('mt-1 block w-20 truncate rounded-md px-1 py-0.5 text-center text-[11px] font-bold', p.pos.length ? 'bg-sunken text-ink' : 'bg-alert-bg text-alert')}>
                          {p.pos.length ? p.pos.map(short).join(' ') : 'Pick orders'}
                        </button>
                      )}
                      <button type="button" onClick={() => remove(p.id)} aria-label={`Remove page ${i + 1}`} className="absolute -right-1.5 -top-1.5 flex h-9 w-9 items-center justify-center rounded-full bg-alert text-bg">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!noNote && <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => cameraRef.current?.click()} disabled={busy || photos.length >= MAX_PHOTOS} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-ink text-[15px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
                  <Camera className="h-5 w-5" /> {photos.length ? 'Next page' : 'Take a photo'}
                </button>
                <button type="button" onClick={() => galleryRef.current?.click()} disabled={busy || photos.length >= MAX_PHOTOS} aria-label="Add from the gallery" className="flex h-12 w-12 items-center justify-center rounded-xl bg-sunken active:bg-line disabled:opacity-40">
                  <Images className="h-5 w-5" />
                </button>
              </div>}
            </section>

            {/* 3. a word, and go */}
            <section className="mt-3 rounded-xl bg-surface p-3">
              <h2 className="text-[13px] font-semibold text-ink2">3 · Note for the chatter (optional)</h2>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Pallet 2 damaged, 3 cartons missing…" className="mt-2 h-11 w-full rounded-xl border border-line bg-surface px-3 text-[15px] outline-none focus:border-ink" />
            </section>

            {/* 4. labels: answers the WAREHOUSE RECEIVED pop-up in Odoo, per order */}
            <section className="mt-3 rounded-xl bg-surface p-3">
              <h2 className="text-[13px] font-semibold text-ink2">4 · Number of labels to print</h2>
              <p className="mt-0.5 text-[12px] text-ink2">Date + PO labels, printed when you save. Empty or 0 prints none.</p>
              {chosenOrders.length === 0 && <p className="mt-2 text-[14px] text-ink2">Pick the orders first.</p>}
              <ul className="mt-2 space-y-1.5">
                {chosenOrders.map((o) => (
                  <li key={o.id} className="flex items-center gap-3">
                    <label htmlFor={`labels-${o.id}`} className="font-cond min-w-0 flex-1 truncate text-[18px] font-bold">{o.name.split(' ')[0]}</label>
                    <input
                      id={`labels-${o.id}`}
                      value={labels[o.id] || ''}
                      onChange={(e) => setLabels((l) => ({ ...l, [o.id]: e.target.value.replace(/\D/g, '').slice(0, 3) }))}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="0"
                      aria-label={`Labels for ${o.name}`}
                      className="h-11 w-24 rounded-xl border border-line bg-surface px-3 text-right text-[17px] font-semibold tabular-nums outline-none focus:border-ink"
                    />
                  </li>
                ))}
              </ul>
            </section>

            {progress && <p className="mt-3 rounded-xl bg-sunken px-3 py-2 text-[13px] text-ink2">{progress}</p>}
            {error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}
            <button type="button" onClick={submit} disabled={busy || !chosen.length || (!noNote && (!photos.length || untagged >= 0 || Boolean(pageless)))} className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-ink text-[17px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
              <FileText className="h-5 w-5" />
              {!chosen.length ? 'Pick the orders first' : noNote ? `Save · no note, ${chosen.length} ${chosen.length === 1 ? 'order' : 'orders'} received` : !photos.length ? 'Take the photos first' : untagged >= 0 ? `Pick the orders for page ${untagged + 1}` : pageless ? `${short(pageless.id)} has no page yet` : `Save · ${photos.length} ${photos.length === 1 ? 'page' : 'pages'}, ${chosen.length} ${chosen.length === 1 ? 'order' : 'orders'} received`}
            </button>
          </>
        )}

        {mode === 'note' && result && (
          <section className="mt-3 rounded-xl bg-surface p-4">
            <div className={cn('flex items-center gap-3 rounded-2xl px-4 py-4', result.error ? 'tone-over bg-tone-bg' : 'tone-out bg-tone-bg')}>
              {result.error ? <AlertTriangle className="pop h-8 w-8 shrink-0 text-tone" /> : <Check className="pop h-8 w-8 shrink-0 text-tone" />}
              <div>
                <p className="font-cond text-[24px] font-bold leading-none text-tone">{result.no_note ? (result.error ? 'Saved, with a problem' : 'Received, office told') : result.error ? 'Partly attached' : 'Attached in Odoo'}</p>
                <p className="mt-1 text-[14px] text-ink2">
                  {result.no_note ? `No delivery note for ${result.po_names.join(', ')}` : `${result.pages} ${result.pages === 1 ? 'page' : 'pages'} on ${result.po_names.join(', ')}`}
                </p>
                <p className="mt-0.5 text-[14px] text-ink2">
                  Warehouse received on {result.warehouse_done} of {result.po_ids.length} {result.po_ids.length === 1 ? 'order' : 'orders'}
                </p>
              </div>
            </div>
            {result.error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{result.error}</p>}
            {result.needs_printer && <PickPrinter />}
            {!result.no_note && <OtherOrders note={result} onChange={replaceNote} checking={!['off', 'failed'].includes(result.read)} />}
            <div className="mt-3 flex flex-wrap gap-2">
              {result.mixed && result.po_ids.map((id, i) => result.pdf_urls?.[id] && (
                <a key={`pdf-${id}`} href={result.pdf_urls[id]} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-xl bg-sunken px-3 text-[14px] font-semibold active:bg-line">
                  <FileText className="h-4 w-4" /> PDF {result.po_names[i].split(' ')[0]}
                </a>
              ))}
              {result.pdf_url && !result.mixed && (
                <a href={result.pdf_url} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-xl bg-sunken px-3 text-[14px] font-semibold active:bg-line">
                  <FileText className="h-4 w-4" /> Open the PDF
                </a>
              )}
              {result.po_ids.map((id, i) => (
                <a key={id} href={result.odoo_urls[id]} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-sunken px-3 text-[14px] font-semibold active:bg-line">
                  {result.po_names[i]} in Odoo <ExternalLink className="h-4 w-4" />
                </a>
              ))}
            </div>
            <button type="button" onClick={() => receive(result)} className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-sunken text-[15px] font-semibold active:bg-line">
              <ScanLine className="h-4 w-4" /> Receive {result.po_ids.length > 1 ? `these ${result.po_ids.length} orders` : result.po_names[0]?.split(' ')[0]}
            </button>
            {!result.no_note && (
              <button type="button" onClick={() => setViewing(result.id)} className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-sunken text-[15px] font-semibold active:bg-line">
                <FileText className="h-4 w-4" /> What the note says
              </button>
            )}
            <button type="button" onClick={reset} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-ink text-[16px] font-semibold text-bg active:opacity-80">
              <Camera className="h-5 w-5" /> New delivery note
            </button>
          </section>
        )}

        {mode === 'note' && recent && (recent.length > 0 || search) && (
          <section className="mt-5">
            <h2 className="mb-1.5 px-1 text-[13px] font-semibold text-ink2">Delivery notes</h2>
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink2" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find by PO, supplier…"
                aria-label="Find a delivery note by order number, supplier or remark"
                className="h-12 w-full rounded-xl bg-surface pl-9 pr-3 text-[16px] outline-none ring-line focus:ring-2"
              />
            </div>
            {recent.length === 0 && <p className="px-1 py-3 text-[14px] text-ink2">No delivery note for “{search.trim()}”.</p>}
            <ul className="space-y-1.5">
              {recent.map((n) => (
                <Fragment key={n.id}>
                  <li className="flex items-center gap-3 rounded-xl bg-surface py-2 pl-3 pr-2">
                    <button type="button" onClick={() => setViewing(n.id)} disabled={n.no_note} className="min-w-0 flex-1 text-left" aria-label={n.no_note ? `No delivery note for ${n.po_names.join(', ')}` : `What the note for ${n.po_names.join(', ')} says`}>
                      <span className="flex items-baseline gap-2">
                        <span className="font-cond truncate text-[18px] font-bold leading-none">{n.po_names.join(' · ')}</span>
                      </span>
                      <span className="mt-1 block truncate text-[12px] text-ink2">
                        {fmtDate(n.created_at)} {timeOf(n.created_at)}{n.user && ` · ${n.user}`}{!n.no_note && ` · ${n.pages} ${n.pages === 1 ? 'page' : 'pages'}`}
                        {n.supplier && ` · ${n.supplier}`}
                        {n.labels != null && ` · ${n.labels} ${n.labels === 1 ? 'label' : 'labels'}`}
                      </span>
                      {n.error && <span className="mt-1 block text-[12px] font-semibold text-alert">{n.error}</span>}
                      <span className={cn('mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold', n.no_note ? 'bg-alert-bg text-alert' : n.read === 'done' ? 'tone-out bg-tone-bg text-tone' : n.read === 'failed' ? 'bg-alert-bg text-alert' : 'bg-sunken text-ink2')}>
                        {n.no_note ? 'No delivery note' : n.read === 'done' ? `${n.lines_read} ${n.lines_read === 1 ? 'line' : 'lines'} read · view` : n.read === 'pending' ? 'Reading…' : n.read === 'failed' ? 'Could not be read · retry' : 'Not read · view'}
                      </span>
                    </button>
                    {n.pdf_url && (
                      <a href={n.pdf_url} target="_blank" rel="noreferrer" className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl bg-sunken px-3 text-[13px] font-semibold active:bg-line" aria-label={`PDF of the note for ${n.po_names.join(', ')}`}>
                        <FileText className="h-4 w-4" /> PDF
                      </a>
                    )}
                    {n.po_ids?.length > 0 && (
                      <button type="button" onClick={() => receive(n)} className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl bg-ink px-3 text-[13px] font-semibold text-bg active:opacity-80" aria-label={`Receive ${n.po_names.join(', ')}`}>
                        <ScanLine className="h-4 w-4" /> Receive
                      </button>
                    )}
                  </li>
                  {n.other_orders?.length > 0 && (
                    <li className="-mt-2.5">
                      <OtherOrders note={n} onChange={replaceNote} />
                    </li>
                  )}
                </Fragment>
              ))}
            </ul>
            {more && (
              <button type="button" onClick={loadOlder} disabled={loadingMore} className="mt-2 flex h-12 w-full items-center justify-center rounded-xl bg-sunken text-[15px] font-semibold active:bg-line disabled:opacity-60">
                {loadingMore ? 'Loading…' : 'Show older notes'}
              </button>
            )}
          </section>
        )}
      </main>

      {tagging && (() => {
        const idx = photos.findIndex((x) => x.id === tagging);
        const photo = photos[idx];
        if (!photo) return null;
        return <PageOrdersSheet key={tagging} photo={photo} index={idx} orders={chosenOrders} initial={photo.pos} onSave={(ids) => saveTags(tagging, ids)} onClose={() => setTagging(null)} />;
      })()}

      {viewing && (
        <DeliveryNoteSheet
          noteId={viewing}
          onClose={() => {
            setViewing(null);
            loadRecent();
          }}
        />
      )}

      {picking && (
        <PickOrdersSheet
          orders={orders}
          initial={chosen}
          title="Orders on this delivery"
          subtitle="Every order the packing list covers. The PDF goes on each of them."
          label={(n) => (n === 0 ? 'Pick the orders' : `Use ${n === 1 ? 'this order' : `these ${n} orders`}`)}
          onClose={() => setPicking(false)}
          onStart={(ids) => {
            setChosen(ids);
            setPicking(false);
          }}
        />
      )}
    </div>
  );
}

/* Mixed pallet: which of the delivery's orders this page belongs to. A new
   page starts with nothing ticked (the floor asked: a carried-over choice was
   too easy to confirm by mistake); a tagged page reopens with its orders. */
function PageOrdersSheet({ photo, index, orders, initial, onSave, onClose }) {
  const [sel, setSel] = useState(() => new Set(initial));
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <Sheet open onClose={onClose} label={`Orders on page ${index + 1}`}>
      <div className="px-4 pb-4 pt-3">
        <div className="flex items-start gap-3">
          <img src={photo.url} alt={`Page ${index + 1}`} className="h-24 w-[4.5rem] shrink-0 rounded-lg object-cover" />
          <div className="min-w-0 flex-1">
            <p className="font-cond text-[24px] font-bold leading-none">Page {index + 1}</p>
            <p className="mt-1 text-[14px] text-ink2">Which orders are on this page? Pick one or more.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 items-center justify-center rounded-full active:bg-sunken">
            <X className="h-5 w-5" />
          </button>
        </div>
        <ul className="mt-3 divide-y divide-line rounded-xl border border-line">
          {orders.map((o) => {
            const on = sel.has(o.id);
            return (
              <li key={o.id}>
                <button type="button" onClick={() => toggle(o.id)} aria-pressed={on} className={cn('flex w-full items-center gap-3 px-3 py-2.5 text-left active:bg-sunken', on && 'bg-sunken')}>
                  <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2', on ? 'border-ink bg-ink text-bg' : 'border-line')}>{on && <Check className="h-4 w-4" strokeWidth={3} />}</span>
                  <span className="min-w-0 flex-1">
                    <span className="font-cond block truncate text-[18px] font-bold leading-none">{o.name}</span>
                    {o.supplier && <span className="block truncate text-[13px] text-ink2">{o.supplier}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <button type="button" onClick={() => onSave([...sel].sort((a, b) => orders.findIndex((o) => o.id === a) - orders.findIndex((o) => o.id === b)))} disabled={!sel.size} className="mt-3 h-14 w-full rounded-xl bg-ink text-[16px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
          {sel.size ? `Page ${index + 1} on ${sel.size === 1 ? orders.find((o) => sel.has(o.id))?.name.split(' ')[0] : `${sel.size} orders`}` : 'Pick at least one order'}
        </button>
      </div>
    </Sheet>
  );
}
